import { useState } from "react";
import PageHero from "@/components/PageHero";
import { api, formatApiErrorDetail, SCHOOL, whatsappShare, STATIC_MODE, submitToNetlify } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle } from "lucide-react";

const CLASSES = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th"];
const NETLIFY_FORM = "admission-enquiry";

export default function AdmissionPage() {
  const [form, setForm] = useState({
    student_name: "",
    parent_name: "",
    student_class: "",
    phone: "",
    email: "",
    address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_name || !form.parent_name || !form.student_class || !form.phone) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
    
        // On Netlify: submit to Netlify Forms → school receives email
        await submitToNetlify(NETLIFY_FORM, form);
     
        
      
      setDone(true);
      toast.success("Admission enquiry submitted! The school has been notified.");
    } catch (e) {
      // Show success path anyway so the parent can complete via WhatsApp
      setDone(true);
      toast.error("Network issue — please tap WhatsApp to send your details directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const waMessage = `Hello Royal English Medium High School,\n\nI just submitted an admission enquiry on your website. Please find my details below:\n\n*Student Name:* ${form.student_name}\n*Parent/Guardian:* ${form.parent_name}\n*Class:* ${form.student_class}\n*Phone:* ${form.phone}${form.email ? `\n*Email:* ${form.email}` : ""}${form.address ? `\n*Address:* ${form.address}` : ""}${form.message ? `\n*Message:* ${form.message}` : ""}\n\nKindly contact me at your earliest convenience.\n\nThank you.`;
    return (
      <div data-testid="admission-success" className="min-h-[70vh] flex items-center justify-center px-6 py-20 bg-white">
        <div className="text-center max-w-lg">
          <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center mb-6">
            <CheckCircle2 size={40} />
          </div>
          <h1 className="font-serif-display text-4xl font-bold text-royal">Thank You!</h1>
          <div className="gold-divider mx-auto" />
          <p className="mt-4 text-gray-600 leading-relaxed">
            Your admission enquiry has been received and forwarded to the school. We've also sent a notification to <span className="font-semibold text-royal">{SCHOOL.email}</span>. Our team will reach out to you on your phone within 24 hours.
          </p>
          <p className="mt-3 text-sm text-gray-500">
            For faster response, tap below to send your details directly via WhatsApp.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={whatsappShare(waMessage)} target="_blank" rel="noreferrer" data-testid="success-whatsapp"
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full font-bold inline-flex items-center gap-2">
              <MessageCircle size={18} /> Send via WhatsApp
            </a>
            <button onClick={() => { setDone(false); setForm({ student_name: "", parent_name: "", student_class: "", phone: "", email: "", address: "", message: "" }); }}
              className="border border-royal text-royal px-6 py-3 rounded-full font-bold hover:bg-royal hover:text-white transition-all">
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admission-page">
      <PageHero eyebrow="ADMISSIONS OPEN" title="Apply for Admission" subtitle="Fill the form below and our team will contact you to begin the admission process." />

      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto bg-[#f7f7f7] rounded-3xl p-8 md:p-12 border border-gray-100 shadow-sm">
          <form
            onSubmit={submit}
            className="grid md:grid-cols-2 gap-5"
            data-testid="admission-form"
            name={NETLIFY_FORM}
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
          >
            <input type="hidden" name="form-name" value={NETLIFY_FORM} />
            <p className="hidden">
              <label>Don't fill this out: <input name="bot-field" /></label>
            </p>
            <div className="md:col-span-2">
              <Label className="font-semibold">Student Name *</Label>
              <Input data-testid="admission-student-name" required name="student_name" value={form.student_name}
                onChange={(e) => setForm({ ...form, student_name: e.target.value })}
                className="mt-1.5 h-11 bg-white" />
            </div>
            <div>
              <Label className="font-semibold">Parent / Guardian Name *</Label>
              <Input data-testid="admission-parent-name" required name="parent_name" value={form.parent_name}
                onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                className="mt-1.5 h-11 bg-white" />
            </div>
            <div>
              <Label className="font-semibold">Class Applying For *</Label>
              <Select value={form.student_class} onValueChange={(v) => setForm({ ...form, student_class: v })}>
                <SelectTrigger data-testid="admission-class" className="mt-1.5 h-11 bg-white">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {CLASSES.map((c) => <SelectItem key={c} value={c}>{c} Standard</SelectItem>)}
                </SelectContent>
              </Select>
              {/* Hidden mirror so Netlify captures the select value */}
              <input type="hidden" name="student_class" value={form.student_class} />
            </div>
            <div>
              <Label className="font-semibold">Phone Number *</Label>
              <Input data-testid="admission-phone" required name="phone" type="tel" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1.5 h-11 bg-white" placeholder="+91 ..." />
            </div>
            <div>
              <Label className="font-semibold">Email (optional)</Label>
              <Input data-testid="admission-email" name="email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 h-11 bg-white" />
            </div>
            <div className="md:col-span-2">
              <Label className="font-semibold">Address</Label>
              <Input data-testid="admission-address" name="address" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="mt-1.5 h-11 bg-white" />
            </div>
            <div className="md:col-span-2">
              <Label className="font-semibold">Message (optional)</Label>
              <Textarea data-testid="admission-message" name="message" rows={4} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1.5 bg-white" />
            </div>
            <div className="md:col-span-2 mt-2">
              <Button data-testid="admission-submit" disabled={submitting} type="submit"
                className="w-full h-12 bg-royal hover:bg-gold hover:text-royal-dark text-white text-base font-bold rounded-full transition-all">
                {submitting ? "Submitting..." : "Submit Application"}
              </Button>
              <p className="text-xs text-gray-500 mt-3 text-center">By submitting, you agree to be contacted by Royal English High School.</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
